import { Link, useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

/**
 * One navigation row.
 *
 * The current page used to be a solid brand-filled pill - the loudest element
 * on the screen, marking the one thing the user is already looking at. It is
 * marked now the way a tab is: a tinted row, brand-coloured label and icon,
 * and a short bar on the left edge.
 */
const NavItem = ({ item }) => {
  const theme = useTheme();
  const location = useLocation();
  const Icon = item.icon;

  // A row owns its URL and everything under it: the movie editor lives at
  // `/movies/edit/:id/:section`, and matching only the last path segment
  // against `item.id` left Movies unlit on every editor tab. The trailing
  // slash keeps `/music` from claiming `/music-video`; `/` matches only itself.
  const { pathname } = location;
  const isSelected =
    item.url === "/"
      ? pathname === "/"
      : pathname === item.url || pathname.startsWith(`${item.url}/`);

  return (
    <Link
      to={item.url}
      style={{ textDecoration: "none", display: "block" }}
    >
      <ListItemButton
        disabled={item.disabled}
        selected={isSelected}
        sx={{
          position: "relative",
          width: "100%",
          borderRadius: 2,
          mb: 0.25,
          minHeight: 38,
          py: 0.75,
          pl: 1.5,
          pr: 1.25,
          alignItems: "center",
          color: "text.secondary",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            color: "text.primary",
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.action.selected,
            color: theme.palette.primary.main,
            "&:hover": { backgroundColor: theme.palette.action.focus },
            // The left marker. 3px, half the row's height, brand red. Drawn
            // inside the row so it lands correctly at both nesting levels.
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: 3,
              height: 18,
              borderRadius: "0 2px 2px 0",
              backgroundColor: theme.palette.primary.main,
            },
          },
        }}
      >
        {item.icon && (
          <ListItemIcon sx={{ minWidth: 30, color: "inherit" }}>
            <Icon
              stroke={1.7}
              size="1.15rem"
              color="currentColor"
            />
          </ListItemIcon>
        )}
        <ListItemText
          disableTypography
          primary={
            <Typography
              color="inherit"
              sx={{ fontSize: "0.8125rem", fontWeight: isSelected ? 600 : 500 }}
            >
              {item.title}
            </Typography>
          }
          secondary={
            item.caption && (
              <Typography
                variant="caption"
                sx={{ ...theme.typography.subMenuCaption }}
                display="block"
              >
                {item.caption}
              </Typography>
            )
          }
        />
        {item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size="small"
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </ListItemButton>
    </Link>
  );
};

export default NavItem;
